import { useReq } from "@larner.dev/use-req";
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Flex,
  Heading,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useCallback, useRef, useState } from "react";
import {
  UpcomingSparkForUser,
  PastSparkForUser,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { apiReq } from "../../../lib/apiReq";
import { VerificationCode } from "../../../components/VerificationCode/VerificationCode";
import { DetailsScreen } from "../../../components/AuthenticationDialog/DetailsScreen";
import { GenderPreferenceScreen } from "../../../components/AuthenticationDialog/GenderPreferenceScreen";
import { ProfilePhotosScreen } from "../../../components/AuthenticationDialog/ProfilePhotosScreen";
import {
  ScreenTransitioner,
  ScreenTransitionerHandle,
} from "../../../components/ScreenTransitioner/ScreenTransitioner";
import { store, useStoreData } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";

interface ExternalEmailVerificationDialogProps {
  isShown: boolean;
  onClose: () => void;
  onComplete: () => void;
  email: string;
  credentialId: number;
  registrationToken: string;
}

function getFirstIncompleteScreen(user: {
  birth_date?: string | null;
  gender?: string | null;
  gender_preference?: string | null;
}): string | null {
  if (!user.birth_date || !user.gender) return "details";
  if (!user.gender_preference) return "genderPreference";
  return "profilePhotos";
}

export const ExternalEmailVerificationDialog = ({
  isShown,
  onClose,
  onComplete,
  email,
  credentialId,
  registrationToken,
}: ExternalEmailVerificationDialogProps) => {
  const storeData = useStoreData();
  const screenTransitionerRef = useRef<ScreenTransitionerHandle>(null);
  const [otp, setOtp] = useState("");
  const [verifyErrors, setVerifyErrors] = useState<string | null>(null);
  const [verifyReq, verifyReqState] = useReq<{
    token: string;
    user: UserWithMediaAndGroups;
    upcomingSparks: UpcomingSparkForUser[];
    pastSparks: PastSparkForUser[];
    requires_verification: false;
  }>(apiReq);

  const goToOrComplete = useCallback(
    (nextScreen: string | null) => {
      if (nextScreen) {
        screenTransitionerRef.current?.setIndex(nextScreen);
      } else {
        onComplete();
      }
    },
    [onComplete]
  );

  const verify = useCallback(
    async (otpOverride?: string) => {
      const otpVal = otpOverride || otp;
      if (!otpVal) {
        setVerifyErrors("Please enter your code");
        return;
      }
      verifyReqState.clearError();
      const response = await verifyReq.post("/external-registration/claim", {
        token: registrationToken,
        otp: `${otpVal}.${credentialId}`,
      });
      if (response.success) {
        // Hydrate the store with the freshly-claimed registration so any
        // reader of upcomingSparks (EventV2, etc.) sees it without a
        // separate /user/state round-trip.
        store.dispatch({
          type: ActionType.UpdateUserState,
          params: {
            user: response.result.user,
            upcomingSparks: response.result.upcomingSparks,
            pastSparks: response.result.pastSparks,
          },
        });
        const nextScreen = storeData.user
          ? getFirstIncompleteScreen(storeData.user)
          : "details";
        goToOrComplete(nextScreen);
      }
    },
    [
      otp,
      verifyReqState,
      verifyReq,
      registrationToken,
      credentialId,
      storeData.user,
      goToOrComplete,
    ]
  );

  const onOtpChange = useCallback(
    (otpVal: string) => {
      setOtp(otpVal);
      if (otpVal.length === 6) {
        verify(otpVal);
      }
    },
    [verify]
  );

  const getNextScreenAfterDetails = (): string | null => {
    if (!storeData.user?.gender_preference) return "genderPreference";
    return "profilePhotos";
  };

  const getNextScreenAfterGenderPreference = (): string | null => {
    return "profilePhotos";
  };

  const screenMap: Record<string, React.ReactNode> = {
    otp: (
      <Flex
        as="form"
        flex={1}
        px="0.25rem"
        direction="column"
        onSubmit={(e: React.FormEvent<Element>) => {
          e.preventDefault();
          verify();
        }}
      >
        <Flex
          flex={1}
          direction="column"
          align="center"
          justify="center"
          gap="1rem"
        >
          <Heading as="h2" size="md">
            Verification Code
          </Heading>
          <Box
            p="3"
            bg="blue.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="blue.200"
            w="100%"
          >
            <Text color="blue.800" fontSize="sm">
              Since you're signing up with a different email than the one we have
              on file, we've sent a verification code to{" "}
              <strong>{email}</strong>. Please enter it below to confirm your
              registration.
            </Text>
          </Box>
          {(verifyErrors || verifyReqState.error) && (
            <Box
              p="3"
              bg="red.50"
              borderRadius="md"
              borderWidth="1px"
              borderColor="red.500"
            >
              <Text color="red.700" fontWeight="700">
                There was a problem validating the code
              </Text>
              <Text color="red.700" fontSize="sm">
                {verifyErrors || verifyReqState.error?.toString()}
              </Text>
            </Box>
          )}
          <VerificationCode value={otp} onChange={onOtpChange} length={6} />
        </Flex>
        <Flex
          py="2rem"
          direction={{ base: "column-reverse", sm: "row" }}
          gap="0.65rem"
        >
          <Button
            variant="ghost"
            flex={1}
            onClick={onClose}
            type="button"
            disabled={verifyReqState.loading}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            colorPalette="greenDark"
            flex={2}
            type="submit"
            size="lg"
            minH="44px"
            disabled={verifyReqState.loading}
          >
            Continue
          </Button>
        </Flex>
      </Flex>
    ),
    details: (
      <DetailsScreen
        onClose={onClose}
        onNext={() => goToOrComplete(getNextScreenAfterDetails())}
      />
    ),
    genderPreference: (
      <GenderPreferenceScreen
        onClose={onClose}
        onComplete={() => goToOrComplete(getNextScreenAfterGenderPreference())}
      />
    ),
    profilePhotos: (
      <ProfilePhotosScreen onClose={onClose} onComplete={onComplete} />
    ),
  };

  return (
    <DialogRoot
      open={isShown}
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      scrollBehavior="inside"
      lazyMount
      unmountOnExit
    >
      <Portal>
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Verify Your Email</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ScreenTransitioner
                ref={screenTransitionerRef}
                minH="100%"
                screens={screenMap}
                startIndex="otp"
              />
            </DialogBody>
            <DialogCloseTrigger />
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};
